import { Table, type TableData, Text, Stack, Loader, Modal, ActionIcon, Group, Affix, Button, Switch } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
// import type { TestEntry } from '@prisma/client';
import { useEffect, useState } from 'react';
import { useAuthHook } from '../../../hooks/auth.hooks.tsx';
import PersonalityReport from '../../../panels/personality.report';
import { DateTime } from 'luxon';
import classes from './styles.module.css';
import { IconEmpathize, IconReportAnalytics, IconRefresh, IconCalendarPlus, IconCalendarMinus } from '@tabler/icons-react';
import TesteeDetails from '../../../panels/testee.details.tsx';
import { useResults } from '../../../contexts/results.ctx.tsx';
import { useProfile } from '../../../contexts/profile.ctx.tsx';


export default function ResultsTable() {
    const inMobile = useMediaQuery('(max-width: 600px)');
    const { accessor } = useAuthHook();
    const { selectedProfile } = useProfile();
    const { getFilteredResultList, getResultList, selectResult } = useResults();
    const [showResultModal, { open, close }] = useDisclosure(false);
    const [modalMode, setModalMode] = useState<'report' | 'details'>('report');
    const [showAll, setShowAll] = useState(false);
    const [sortAsc, setSortAsc] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [data, setData] = useState<TableData>({
        caption: 'Client Results',
        head: ['Name', 'Type', 'Date', 'Action'],
        body: []
    });

    const [selectedResult, setSelectedResult] = useState<{ cType: string, uname: string, testID: string } | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        const resLst = getFilteredResultList(selectedProfile?.testAccessId);
        makeTableData(resLst);
        setLoading(false);
    }, [accessor, selectedProfile]);

    useEffect(() => {
        const resLst = getFilteredResultList(showAll ? undefined : selectedProfile?.testAccessId, sortAsc);
        makeTableData(resLst);
    }, [showAll, sortAsc]);

    const makeTableData = (resultList: ReturnType<typeof getFilteredResultList>) => {
        setData(prev => ({
            ...prev,
            body: resultList.map(r => [
                r.testeeName,
                r.result,
                // @ts-ignore
                DateTime.fromISO(r.createdAt as string).toLocaleString(DateTime.DATETIME_MED),
                <Group gap={'10px'} justify='center'>
                    <ActionIcon size={'lg'} onClick={() => {
                        setSelectedResult({ cType: r.result, uname: r.testeeName, testID: r.id });
                        selectResult(r);
                    }}><IconReportAnalytics /></ActionIcon>
                    <ActionIcon size={'lg'} variant={'outline'} onClick={() => {
                        setSelectedResult({ cType: r.result, uname: r.testeeName, testID: r.id });
                        setModalMode('details');
                        open();
                    }}>
                        <IconEmpathize />
                    </ActionIcon>
                </Group>
            ])
        }));
    }

    // useEffect(() => {
    //     if (resultList.length > 0) {
    //         setData(prev => ({
    //             ...prev,
    //             body: resultList.map(r => [
    //                 r.testeeName,
    //                 r.result,
    //                 DateTime.fromJSDate(r.createdAt).toLocaleString(DateTime.DATETIME_MED),
    //                 <Group gap={'10px'} justify='center'>
    //                     <ActionIcon size={'lg'} onClick={() => {
    //                         setSelectedResult({ cType: r.result, uname: r.testeeName, testID: r.id });
    //                         setModalMode('report');
    //                         open();
    //                     }}><IconReportAnalytics /></ActionIcon>
    //                     <ActionIcon size={'lg'} variant={'outline'} onClick={() => {
    //                         setSelectedResult({ cType: r.result, uname: r.testeeName, testID: r.id });
    //                         setModalMode('details');
    //                         open();
    //                     }}>
    //                         <IconEmpathize />
    //                     </ActionIcon>
    //                 </Group>
    //             ])
    //         }));
    //     }
    // }, [resultList]);

    return (<>
        <Modal
            opened={showResultModal}
            title={<Text fw={300} size="1.6rem" p={'md'}>
                {selectedResult?.uname} - {selectedResult?.cType}
            </Text>}
            onClose={close}
            size={'xl'}
            centered
            zIndex={5000}
            styles={{
                root: { position: 'fixed', top: 0, left: 0 },
                overlay: { opacity: 0.7 },
                content: {
                    padding: '20px',
                    minHeight: '300px'
                }
            }}
        >
            {/* @ts-ignore */}
            {modalMode === 'report' && <PersonalityReport clientName={selectedResult?.uname || ''} clientType={selectedResult?.cType || ''} agentType={selectedProfile?.testResult || ''} testID={selectedResult?.testID || ''} />}
            {modalMode === 'details' && <TesteeDetails testID={selectedResult?.testID || ''} />}
        </Modal>

        <Affix position={{ 
            top: inMobile ? 'auto' : 80, 
            right: 20,
            bottom: inMobile ? 40 : 'auto'
        }}>
            {!inMobile && <Button onClick={() => {
                getResultList(true);
            }}>
                <IconRefresh />
                <Text>Refresh Results</Text>
            </Button>}
            {inMobile && <ActionIcon 
                onClick={() => {
                    getResultList(true);
                }}
                size={'4rem'}
                radius={'xl'}
            >
                <IconRefresh size={'2.5rem'} />
            </ActionIcon>}
        </Affix>

        <Stack justify='center'>
            <Group justify='space-between'>
                <div></div>
                <Text size='xl' fw={300}>
                    {showAll ? 'All ' : 'Your '} 
                    Client Tests
                    {sortAsc ? ' (Newest First)' : ' (Oldest First)'}
                </Text>
                <Group>
                    <ActionIcon 
                        onClick={() => setSortAsc(!sortAsc)}
                        variant={sortAsc ? 'light' : 'filled'}
                        size={'lg'}
                    >
                        {sortAsc ? <IconCalendarPlus /> : <IconCalendarMinus />}
                    </ActionIcon>
                    <Switch label='Show All' checked={showAll} onChange={() => setShowAll(!showAll)} />

                </Group>
            </Group>
            {loading && <Loader />}
            {error && <Text>Error: {error.message}</Text>}
            {data.body!.length > 0 && <Table className={classes.tableCorrect} data={data} striped highlightOnHover />}
            {data.body!.length === 0 && <Text>No results yet found</Text>}
        </Stack></>)
}